import React, {useCallback, useMemo, useState} from 'react';
import {alpha, styled, useTheme} from '@mui/material';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import Linkify from 'linkify-react';
import {MatrixEvent} from 'matrix-js-sdk';
import {useTranslation} from 'react-i18next';
import SenderName from './sender-name';
import {format} from 'date-fns';
import {useAppSelector} from '@/redux-store/store';
import MessageOptionsList from '@/components/matrix-chat/ui/menu-options-list';
import {useMatrixRoomContext} from '@/contexts/matrix/room';
import RenderWithOptionalTooltip from '@/components/ui/optional-tooltip';
import {useMatrixContext} from '@/contexts/matrix/client';
import {extractLastTextMessage} from '@/services/matrix/messages';

const ReplyContainer = styled('div')<{isOwn: boolean; isDark: boolean}>(
  ({theme, isOwn, isDark}) => ({
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '18px 18px 18px 18px',
    marginTop: theme.spacing(3),
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    position: 'relative',
    zIndex: 1,
    backgroundColor: isDark
      ? isOwn
        ? theme.palette.primary.main
        : theme.palette.secondary.dark
      : isOwn
      ? theme.palette.primary.main
      : theme.palette.secondary.light
  })
);

const QuotedMessagePart = styled('div')<{isDark: boolean; isOwn: boolean}>(
  ({theme, isDark, isOwn}) => ({
    borderRadius: '10px 10px 10px 10px',

    paddingBottom: theme.spacing(1),
    backgroundColor: isDark
      ? isOwn
        ? alpha(theme.palette.primary.light, 0.5)
        : alpha(theme.palette.primary.light, 0.5)
      : isOwn
      ? alpha(theme.palette.primary.dark, 0.5)
      : alpha(theme.palette.primary.light, 0.5),
    padding: theme.spacing(1),
    position: 'relative',
    zIndex: 2,
    alignItems: 'center',
    justifyContent: 'center',
    width: '80%',
    margin: '0 auto',
    marginTop: theme.spacing(2),
    color: isOwn
      ? theme.palette.customColors?.messageFontOwn
      : theme.palette.customColors?.messageFont
  })
);

const ReplyPart = styled('div')<{isOwn: boolean; isDark: boolean}>(
  ({theme, isOwn, isDark}) => ({
    backgroundColor: isDark
      ? isOwn
        ? theme.palette.primary.main
        : theme.palette.secondary.dark
      : isOwn
      ? theme.palette.primary.main
      : theme.palette.secondary.light,
    padding: theme.spacing(1),
    cursor: 'pointer',
    borderRadius: '0 0 18px 18px',
    color: theme.palette.primary.contrastText
  })
);

interface ReplyProps {
  onDelete?: () => void;
  onQuote: () => void;
  wholeReplyMessageContent: MatrixEvent;
}

const Reply: React.FC<ReplyProps> = ({
  onDelete,
  onQuote,
  wholeReplyMessageContent
}) => {
  const theme = useTheme();
  const {t} = useTranslation();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const isDarkMode = useAppSelector((store) => store.theme.darkMode);

  const {setActiveMatrixEvent, matrixEvents} = useMatrixRoomContext();
  const {matrixUserId} = useMatrixContext();

  const memberId = matrixUserId;

  const replyDetails = useMemo(() => {
    //console.log('use memo triggered');
    const messageContent = wholeReplyMessageContent.event.content?.body;

    const isMessageDeleted = (messageId: string, message?: MatrixEvent) => {
      if (message) {
        const effectiveEventToCheck = message.getEffectiveEvent();

        return (
          (effectiveEventToCheck.content?.reason &&
            effectiveEventToCheck.content.reason.includes(
              'User requested deletion.'
            )) ||
          Object.keys(effectiveEventToCheck.content ?? {}).length === 0
        );
      } else {
        const deletionEvent = matrixEvents.find((event) => {
          const effectiveEvent = event.getEffectiveEvent();
          return (
            effectiveEvent.type === 'm.room.redaction' &&
            effectiveEvent.content?.redacts === messageId &&
            effectiveEvent.content?.reason?.includes('User requested deletion.')
          );
        });

        return deletionEvent !== undefined;
      }
    };

    if (messageContent) {
      const match = messageContent.match(
        /\[\[QUOTE id="(.*?)"\]\]<@(.*?)>:(.*?)\[\[\/QUOTE\]\](.*)/
      );
      if (match) {
        const originalEventId = match[1];
        const senderId = match[2];

        const quotedMessageText = match[3].trim();
        const replyText = match[4].trim();

        const originalMessage = matrixEvents.find((event) => {
          const effectiveEvent = event.getEffectiveEvent();
          return (
            effectiveEvent.event_id === originalEventId ||
            effectiveEvent.content?.redacts === originalEventId
          );
        });

        if (isMessageDeleted(originalEventId, originalMessage)) {
          return {
            originalMessage: null,
            quotedMessageText: 'NTF_MESSAGE_DELETED',
            replyText,
            senderId
          };
        } else {
          return {originalMessage, quotedMessageText, replyText, senderId};
        }
      }
    }

    return null;
  }, [matrixEvents, wholeReplyMessageContent]);

  const replyMessage = replyDetails?.replyText;

  const isDeletedMessage = replyDetails?.replyText === 'NTF_MESSAGE_DELETED';

  const wholeReplyMessageEffectiveEvent =
    wholeReplyMessageContent.getEffectiveEvent();

  const timestamp = wholeReplyMessageEffectiveEvent.origin_server_ts;

  const isOwnReplyMessage = wholeReplyMessageEffectiveEvent.sender === memberId;

  const isOwnQuotedMessage = replyDetails?.senderId === memberId;

  const senderId = isOwnQuotedMessage ? t('you') : replyDetails?.senderId;

  const quotedMessageText = extractLastTextMessage(
    replyDetails?.quotedMessageText
  );

  const canBeDeleted = !isDeletedMessage && isOwnReplyMessage;

  const formatTime = useCallback((timestamp: number): string => {
    const date = new Date(timestamp);
    return format(date, 'HH:mm');
  }, []);

  const getDisplayedQuotedText = useCallback(() => {
    if (quotedMessageText === 'NTF_MESSAGE_DELETED') {
      return t('NTF_MESSAGE_DELETED');
    }
    return quotedMessageText;
  }, [quotedMessageText, t]);
  const truncateText = useCallback((text: string): string => {
    const maxLength = 150;
    if (text.length > maxLength) {
      return `${text.substring(0, maxLength)}...`;
    }
    return text;
  }, []);

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      if (!isDeletedMessage) {
        setMenuAnchorEl(event.currentTarget);
        setActiveMatrixEvent(wholeReplyMessageContent);
      }
    },
    [isDeletedMessage, wholeReplyMessageContent, setActiveMatrixEvent]
  );

  const handleCloseMenu = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  return (
    <>
      <ReplyContainer isOwn={isOwnReplyMessage} isDark={isDarkMode}>
        <QuotedMessagePart isDark={isDarkMode} isOwn={isOwnReplyMessage}>
          {typeof senderId === 'string' && (
            <SenderName
              senderId={senderId}
              additionalMarginBottom={1}
              sx={{
                color: isOwnReplyMessage
                  ? theme.palette.customColors?.messageFontSenderOwn
                  : theme.palette.customColors?.messageFontSender
              }}
            />
          )}

          <ResponsiveTypography
            smallAllSizes
            sx={{
              fontStyle: 'italic'
            }}
          >
            <Linkify
              options={{
                target: '_blank',
                rel: 'noopener noreferrer',
                attributes: {
                  style: {
                    color: theme.palette.customColors?.urlLink
                  }
                }
              }}
            >
              {truncateText(getDisplayedQuotedText())}
            </Linkify>
          </ResponsiveTypography>
        </QuotedMessagePart>
        <RenderWithOptionalTooltip
          shoudShowTooltip={!isDeletedMessage}
          tooltipText={t('more_options')}
          showArrow
        >
          <ReplyPart
            isOwn={isOwnReplyMessage}
            isDark={isDarkMode}
            onClick={handleOpenMenu}
          >
            <ResponsiveTypography>{replyMessage}</ResponsiveTypography>
            <div
              style={{
                textAlign: 'right',
                width: '100%'
              }}
            >
              <ResponsiveTypography
                smallAllSizes={true}
                sx={{fontWeight: 'bold'}}
              >
                {timestamp && formatTime(timestamp)}
              </ResponsiveTypography>
            </div>
          </ReplyPart>
        </RenderWithOptionalTooltip>
      </ReplyContainer>
      <MessageOptionsList
        anchorEl={menuAnchorEl}
        onClose={handleCloseMenu}
        onQuote={onQuote}
        canDelete={canBeDeleted}
        onDelete={canBeDeleted ? onDelete : undefined}
      />
    </>
  );
};

export default Reply;
